import { useState } from 'react';
import { CheckCircle2, Send, Loader2, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { PublicFileDropzone } from '@/components/PublicFileDropzone';
import { PublicPhoneInput, publicPhoneValue } from '@/components/PublicPhoneInput';
import { useLang } from '@/i18n/LangContext';
import { completePublicContactRequest, submitPublicForm, uploadPublicContactFile } from '@/lib/publicForms';

const MAX_FILES = 5;

const usePublicSubmit = () => {
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const submit = async (payload, files = []) => {
    setState('sending');
    setError('');
    try {
      const request = await submitPublicForm('/public/v1/contact-requests', {
        ...payload,
        attachments: files.map((file) => ({ file_name: file.name, mime_type: file.type, size_bytes: file.size })),
      });
      if (!request.public_token) throw new Error('La conferma della richiesta è temporaneamente non disponibile');
      for (const file of files) await uploadPublicContactFile(request.public_token, file);
      await completePublicContactRequest(request.public_token);
      setState('sent');
    } catch (requestError) {
      setState('error');
      setError(requestError.message);
    }
  };
  const reset = () => {
    setState('idle');
    setError('');
  };
  return { state, submit, error, reset };
};

export const SuccessPanel = ({ title = 'Richiesta inviata', text = 'Grazie! Ti ricontatteremo al più presto per approfondire la tua situazione.', testid, onNewRequest, newRequestLabel = 'Apri una nuova richiesta' }) => (
  <div data-testid={testid} className="border border-primary/30 bg-brand-pale p-6 md:p-8">
    <CheckCircle2 className="h-8 w-8 text-primary mb-4" />
    <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
    <p className="text-sm text-foreground/70 leading-relaxed">{text}</p>
    {onNewRequest && (
      <button type="button" onClick={onNewRequest} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-white px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:border-primary">
        <Plus className="h-4 w-4" /> {newRequestLabel}
      </button>
    )}
  </div>
);

const PrivacyCheck = ({ checked, onChange, dark, id, text }) => (
  <div className="flex items-start gap-3">
    <Checkbox id={id} data-testid={`${id}-checkbox`} checked={checked} onCheckedChange={onChange} className="mt-0.5 rounded-sm" />
    <Label htmlFor={id} className={`text-xs leading-relaxed cursor-pointer ${dark ? 'text-white/70' : 'text-muted-foreground'}`}>
      {text}
    </Label>
  </div>
);

const FilePicker = ({ files, setFiles, dark = false, lang = 'it' }) => {
  return (
    <PublicFileDropzone
      id={dark ? 'quick-contact-files' : 'contact-files'}
      files={files}
      onFilesChange={setFiles}
      maxFiles={MAX_FILES}
      dark={dark}
      lang={lang}
      testId="contact-form-upload"
      label={lang === 'en' ? 'Attachments (optional)' : 'Allegati (facoltativi)'}
    />
  );
};

export const QuickContactForm = ({ dark = false }) => {
  const { state, submit, error, reset } = usePublicSubmit();
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [files, setFiles] = useState([]);
  const { t, lang } = useLang();

  const onSubmit = (event) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    submit({
      name: String(values.get('name') || '').trim(),
      email: String(values.get('email') || '').trim(),
      message: String(values.get('message') || '').trim(),
      customer_type: 'UNKNOWN', source: 'QUICK_FORM', locale: lang === 'en' ? 'en-GB' : 'it-CH',
      privacy_notice_version: '2026-08', privacy_consent: true, marketing_consent: marketing,
      website: '',
    }, files);
  };

  const newRequest = () => {
    setPrivacy(false);
    setMarketing(false);
    setFiles([]);
    reset();
  };

  if (state === 'sent') {
    return dark ? (
      <div data-testid="quick-form-success" className="space-y-3">
        <p className="text-sm text-brand-light flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> {t('quickSuccess')}</p>
        <button type="button" onClick={newRequest} className="inline-flex items-center gap-2 rounded-lg border border-white/25 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10">
          <Plus className="h-3.5 w-3.5" /> {lang === 'en' ? 'Start a new request' : 'Apri una nuova richiesta'}
        </button>
      </div>
    ) : (
      <SuccessPanel testid="quick-form-success" onNewRequest={newRequest} newRequestLabel={lang === 'en' ? 'Start a new request' : 'Apri una nuova richiesta'} />
    );
  }

  return (
    <form data-testid="quick-contact-form" onSubmit={onSubmit} className="space-y-3">
      <Input name="name" data-testid="quick-form-name" required placeholder={t('quickNomePh')} className={dark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-lg' : 'rounded-lg'} />
      <Input name="email" type="email" data-testid="quick-form-contact" required autoComplete="email" placeholder="Email" className={dark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-lg' : 'rounded-lg'} />
      <Textarea name="message" data-testid="quick-form-message" required placeholder={t('quickMsgPh')} rows={3} className={dark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-lg resize-none' : 'rounded-lg resize-none'} />
      <FilePicker files={files} setFiles={setFiles} dark={dark} lang={lang} />
      <div className={dark ? '[&_label]:text-white/70' : ''}>
        <PrivacyCheck id="quick-privacy" checked={privacy} onChange={setPrivacy} dark={dark} text={t('formPrivacy')} />
      </div>
      <PrivacyCheck id="quick-marketing" checked={marketing} onChange={setMarketing} dark={dark} text={lang === 'en' ? 'I would like to receive useful updates by email (optional).' : 'Desidero ricevere aggiornamenti utili via email (facoltativo).'} />
      {error && <p role="alert" className={`text-xs ${dark ? 'text-amber-200' : 'text-destructive'}`}>{error}</p>}
      <button
        type="submit"
        data-testid="quick-form-submit"
        disabled={!privacy || state === 'sending'}
        className="w-full inline-flex items-center justify-center gap-2 bg-brand-light text-brand-deep px-4 py-2.5 text-sm font-semibold rounded-lg hover:bg-white transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {state === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {t('formInviaMsg')}
      </button>
    </form>
  );
};

export const FullContactForm = () => {
  const { state, submit, error, reset } = usePublicSubmit();
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState('+41');
  const [phoneNumber, setPhoneNumber] = useState('');
  const { t, lang } = useLang();
  const topics = t('topics');

  const onSubmit = (event) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    submit({
      name: String(values.get('name') || '').trim(),
      email: String(values.get('email') || '').trim(), phone: publicPhoneValue(phonePrefix, phoneNumber) || null,
      topic: String(values.get('topic') || ''), message: String(values.get('message') || '').trim(),
      source: 'CONTACT_FORM', locale: lang === 'en' ? 'en-GB' : 'it-CH', privacy_notice_version: '2026-08',
      privacy_consent: true, marketing_consent: marketing, website: '',
    });
  };

  const newRequest = () => {
    setPrivacy(false);
    setMarketing(false);
    setPhonePrefix('+41');
    setPhoneNumber('');
    reset();
  };

  if (state === 'sent') return <SuccessPanel testid="contact-form-success" title={t('successTitle')} text={t('successText')} onNewRequest={newRequest} newRequestLabel={lang === 'en' ? 'Start a new request' : 'Apri una nuova richiesta'} />;

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="cf-nome" className="text-sm font-bold mb-1.5 block">{lang === 'en' ? 'Full name or company' : 'Nome completo o azienda'}</Label>
          <Input name="name" id="cf-nome" data-testid="contact-form-nome" required autoComplete="name" placeholder={lang === 'en' ? 'Jane Smith or Company Ltd' : 'Mario Rossi oppure Azienda SA'} className="rounded-lg h-11" />
        </div>
        <div>
          <Label htmlFor="cf-email" className="text-sm font-bold mb-1.5 block">{t('formEmail')}</Label>
          <Input name="email" id="cf-email" data-testid="contact-form-email" type="email" required autoComplete="email" placeholder="nome@esempio.ch" className="rounded-lg h-11" />
        </div>
        <PublicPhoneInput id="cf-telefono" label={t('formTelefono')} optional lang={lang} prefix={phonePrefix} number={phoneNumber} onPrefixChange={setPhonePrefix} onNumberChange={setPhoneNumber} autoComplete="tel" testId="contact-form-telefono" />
        <div>
          <Label htmlFor="cf-argomento" className="text-sm font-bold mb-1.5 block">{lang === 'en' ? 'Subject' : 'Oggetto'}</Label>
          <select name="topic" id="cf-argomento" data-testid="contact-form-argomento" required className="w-full h-11 border border-input bg-white px-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{t('selectDefault')}</option>
            {topics.map((topic) => <option key={topic}>{topic}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label htmlFor="cf-messaggio" className="text-sm font-bold mb-1.5 block">{lang === 'en' ? 'Description' : 'Descrizione'}</Label>
        <Textarea name="message" id="cf-messaggio" data-testid="contact-form-messaggio" required rows={5} placeholder={t('formMessaggioPh')} className="rounded-lg resize-none" />
      </div>

      <PrivacyCheck id="contact-privacy" checked={privacy} onChange={setPrivacy} text={t('formPrivacy')} />
      <PrivacyCheck id="contact-marketing" checked={marketing} onChange={setMarketing} text={lang === 'en' ? 'I would like to receive useful updates by email (optional).' : 'Desidero ricevere aggiornamenti utili via email (Facoltativo).'} />
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      <button
        type="submit"
        data-testid="contact-form-submit"
        disabled={!privacy || state === 'sending'}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 text-sm font-semibold rounded-lg hover:bg-brand-dark transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {state === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {t('formInvia')}
      </button>
    </form>
  );
};
